import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faStopwatch,
    faHourglassHalf,
    faTruck,
    faInfoCircle,
    faRoute,
    faPause
} from '@fortawesome/free-solid-svg-icons';

const DeliveryTimeAnalytics = ({ status_time, DELIVERY_STATUS, two_way = false }) => {
    if (!status_time || Object.keys(status_time).length === 0) {
        return null;
    }

    const calculateTimeAnalytics = () => {
        const analytics = {
            responseTime: null,
            pickupTime: null,
            deliveryTime: null,
            returnTime: null,
            totalDuration: null,
            travelToPickup: null,
            travelToDropoff: null,
            travelToReturn: null,
            waitTime: null
        };

        try {
            // Helper function to calculate time difference in minutes
            const calculateTimeDiff = (startTime, endTime) => {
                if (!startTime || !endTime) return null;
                const start = new Date(startTime);
                const end = new Date(endTime);
                const diffMs = end - start;
                const diffMinutes = diffMs / (1000 * 60);
                // Return at least 1 minute for very small differences
                return Math.max(1, Math.round(diffMinutes));
            };

            // Response Time: From request to driver assignment
            if (status_time[DELIVERY_STATUS.REQUESTED] && status_time[DELIVERY_STATUS.ASSIGNED]) {
                analytics.responseTime = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.REQUESTED],
                    status_time[DELIVERY_STATUS.ASSIGNED]
                );
            }

            // Pickup Time: From assignment to pickup arrival
            if (status_time[DELIVERY_STATUS.ASSIGNED] && status_time[DELIVERY_STATUS.PICKUP_ARRIVED]) {
                analytics.pickupTime = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.ASSIGNED],
                    status_time[DELIVERY_STATUS.PICKUP_ARRIVED]
                );
            }

            // Delivery Time: From pickup to dropoff
            if (status_time[DELIVERY_STATUS.PICKUP_ARRIVED] && status_time[DELIVERY_STATUS.DROPOFF_ARRIVED]) {
                analytics.deliveryTime = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.PICKUP_ARRIVED],
                    status_time[DELIVERY_STATUS.DROPOFF_ARRIVED]
                );
            }

            // Return Time: From dropoff to return (only for two-way trips)
            if (two_way && status_time[DELIVERY_STATUS.DROPOFF_ARRIVED] && status_time[DELIVERY_STATUS.RETURN_ARRIVED]) {
                analytics.returnTime = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.DROPOFF_ARRIVED],
                    status_time[DELIVERY_STATUS.RETURN_ARRIVED]
                );
            }

            // Wait Time: Time spent waiting at dropoff location (for two-way trips)
            if (two_way && status_time[DELIVERY_STATUS.DROPOFF_ARRIVED] && status_time[DELIVERY_STATUS.EN_ROUTE_RETURN]) {
                analytics.waitTime = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.DROPOFF_ARRIVED],
                    status_time[DELIVERY_STATUS.EN_ROUTE_RETURN]
                );
            }

            // Total Duration: From request to completion
            if (status_time[DELIVERY_STATUS.REQUESTED] && status_time[DELIVERY_STATUS.COMPLETED]) {
                analytics.totalDuration = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.REQUESTED],
                    status_time[DELIVERY_STATUS.COMPLETED]
                );
            }

            // Travel to Pickup
            if (status_time[DELIVERY_STATUS.HEADING_TO_PICKUP] && status_time[DELIVERY_STATUS.PICKUP_ARRIVED]) {
                analytics.travelToPickup = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.HEADING_TO_PICKUP],
                    status_time[DELIVERY_STATUS.PICKUP_ARRIVED]
                );
            }

            // Travel to Dropoff
            if (status_time[DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF] && status_time[DELIVERY_STATUS.DROPOFF_ARRIVED]) {
                analytics.travelToDropoff = calculateTimeDiff(
                    status_time[DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF],
                    status_time[DELIVERY_STATUS.DROPOFF_ARRIVED]
                );
            }

            // Travel to Return (only for two-way trips)
            if (two_way && status_time[DELIVERY_STATUS.EN_ROUTE_RETURN] && status_time[DELIVERY_STATUS.RETURN_ARRIVED]) {
                const travelStart = new Date(status_time[DELIVERY_STATUS.EN_ROUTE_RETURN]);
                const travelEnd = new Date(status_time[DELIVERY_STATUS.RETURN_ARRIVED]);
                analytics.travelToReturn = Math.round((travelEnd - travelStart) / (1000 * 60));
            }

        } catch (error) {
            console.error('Error calculating delivery time analytics:', error);
        }

        return analytics;
    };

    const analytics = calculateTimeAnalytics();

    const formatTime = (minutes) => {
        if (minutes === null || minutes === undefined) return 'N/A';
        if (minutes === 0) return 'N/A';
        if (minutes < 1) return '< 1 min';
        if (minutes < 60) return `${Math.round(minutes)} min`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    const getTimeQuality = (time, threshold) => {
        if (time === null || time === undefined) return 'neutral';
        return time <= threshold ? 'good' : 'warning';
    };

    return (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-purple-50 to-pink-50 p-4 sm:p-6 rounded-lg border border-purple-200">
            <h4 className="text-gray-700 font-semibold mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                <FontAwesomeIcon icon={faTruck} className="mr-2 text-purple-600" />
                Delivery Time Analytics
                {two_way && (
                    <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        Two-Way Trip
                    </span>
                )}
                {!two_way && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        One-Way Trip
                    </span>
                )}
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* Response Time */}
                {analytics.responseTime && analytics.responseTime > 0 && (
                    <div className={`p-3 rounded-lg border ${getTimeQuality(analytics.responseTime, 5) === 'good'
                        ? 'bg-green-50 border-green-200'
                        : getTimeQuality(analytics.responseTime, 5) === 'warning'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faClock} className="text-blue-600" />
                            <span className="font-semibold text-sm">Response Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.responseTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Request to assignment
                        </div>
                    </div>
                )}

                {/* Pickup Time */}
                {analytics.pickupTime && analytics.pickupTime > 0 && (
                    <div className={`p-3 rounded-lg border ${getTimeQuality(analytics.pickupTime, 25) === 'good'
                        ? 'bg-green-50 border-green-200'
                        : getTimeQuality(analytics.pickupTime, 25) === 'warning'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faHourglassHalf} className="text-orange-600" />
                            <span className="font-semibold text-sm">Pickup Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.pickupTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Assignment to pickup
                        </div>
                    </div>
                )}

                {/* Delivery Time */}
                {analytics.deliveryTime && analytics.deliveryTime > 0 && (
                    <div className="p-3 rounded-lg border bg-purple-50 border-purple-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faTruck} className="text-purple-600" />
                            <span className="font-semibold text-sm">Delivery Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.deliveryTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Pickup to dropoff
                        </div>
                    </div>
                )}

                {/* Return Time - Only for Two-Way */}
                {two_way && analytics.returnTime && analytics.returnTime > 0 && (
                    <div className="p-3 rounded-lg border bg-pink-50 border-pink-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faRoute} className="text-pink-600" />
                            <span className="font-semibold text-sm">Return Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.returnTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Dropoff to return
                        </div>
                    </div>
                )}

                {/* Wait Time - Only for Two-Way */}
                {two_way && analytics.waitTime && analytics.waitTime > 0 && (
                    <div className="p-3 rounded-lg border bg-yellow-50 border-yellow-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faPause} className="text-yellow-600" />
                            <span className="font-semibold text-sm">Wait Time</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.waitTime)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Waiting at dropoff
                        </div>
                    </div>
                )}

                {/* Travel to Pickup */}
                {analytics.travelToPickup && analytics.travelToPickup > 0 && (
                    <div className="p-3 rounded-lg border bg-indigo-50 border-indigo-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faRoute} className="text-indigo-600" />
                            <span className="font-semibold text-sm">Travel to Pickup</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.travelToPickup)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Driver to pickup
                        </div>
                    </div>
                )}

                {/* Travel to Dropoff */}
                {analytics.travelToDropoff && analytics.travelToDropoff > 0 && (
                    <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faRoute} className="text-blue-600" />
                            <span className="font-semibold text-sm">Travel to Dropoff</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.travelToDropoff)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Pickup to dropoff
                        </div>
                    </div>
                )}

                {/* Travel to Return - Only for Two-Way */}
                {two_way && analytics.travelToReturn && analytics.travelToReturn > 0 && (
                    <div className="p-3 rounded-lg border bg-red-50 border-red-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faRoute} className="text-red-600" />
                            <span className="font-semibold text-sm">Travel to Return</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.travelToReturn)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Dropoff to pickup location
                        </div>
                    </div>
                )}

                {/* Total Duration */}
                {analytics.totalDuration && analytics.totalDuration > 0 && (
                    <div className="p-3 rounded-lg border bg-gray-50 border-gray-200">
                        <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faStopwatch} className="text-gray-600" />
                            <span className="font-semibold text-sm">Total Duration</span>
                        </div>
                        <div className="text-lg font-bold text-gray-800 mt-1">
                            {formatTime(analytics.totalDuration)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                            Request to completion
                        </div>
                    </div>
                )}

                {/* No Analytics Available */}
                {(!analytics.responseTime || analytics.responseTime <= 0) &&
                    (!analytics.pickupTime || analytics.pickupTime <= 0) &&
                    (!analytics.deliveryTime || analytics.deliveryTime <= 0) && (
                        <div className="col-span-full p-4 text-center text-gray-500">
                            <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                            No time analytics available yet. Analytics will appear as the delivery progresses.
                        </div>
                    )}
            </div>

            {/* Trip Type Summary */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600" />
                    <span className="font-semibold text-sm text-gray-700">Trip Summary</span>
                </div>
                <div className="text-sm text-gray-600">
                    {two_way ? (
                        <div>
                            <strong>Two-Way Trip:</strong> Pickup → Dropoff → Wait → Return to Pickup Location
                        </div>
                    ) : (
                        <div>
                            <strong>One-Way Trip:</strong> Pickup → Dropoff (No Return)
                        </div>
                    )}
                </div>
            </div>

            {/* Performance Indicators */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2 mb-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600" />
                    <span className="font-semibold text-sm text-gray-700">Performance Indicators</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className={`px-2 py-1 rounded ${getTimeQuality(analytics.responseTime, 5) === 'good' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                        Response: {getTimeQuality(analytics.responseTime, 5) === 'good' ? '✓ Good' : '⚠ Slow'}
                    </div>
                    <div className={`px-2 py-1 rounded ${getTimeQuality(analytics.pickupTime, 25) === 'good' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                        Pickup: {getTimeQuality(analytics.pickupTime, 25) === 'good' ? '✓ Good' : '⚠ Long'}
                    </div>
                    <div className="px-2 py-1 rounded bg-purple-100 text-purple-800">
                        Delivery: {analytics.deliveryTime ? '✓ Complete' : '⏳ In Progress'}
                    </div>
                    <div className="px-2 py-1 rounded bg-gray-100 text-gray-600">
                        Total: {analytics.totalDuration ? '✓ Complete' : '⏳ Ongoing'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryTimeAnalytics; 